import React from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import getFormStateHandler from 'services/forms/getFormStateHandler';
import getShownDifference from 'services/numbers/getShownDifference';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';
import { MAX_QUANTITY_DIGITS } from 'services/offers/inputLimits';
import IVersion from '../../IVersion';
import getVersionInstallationContract from '../getVersionInstallationContract';
import { getMarginClass } from '../getMarginRatioClassName';
import NumberWithDropdownInputField from '../../../../../layout/forms/NumberWithDropdownInputField';
import getUnitOptions from '../../../../services/getUnitOptions';
import ICalculationReview from '../../ICalculationReview';
import InputLabel from '../../../../../layout/forms/InputLabel';
import { H } from '../../../../../../services/enums/units';
import numericStringToFloat from '../../../../../../services/numbers/numericStringToFloat';
import TrashIcon from '../../../../../layout/icons/TrashIcon';

const BatchCalculationReview = ({
  locked,
  productUnit,
  quantity,
  setCalculationReview,
  removeCalculationReview,
  version,
  calculationReview,
  priceSummary,
}: {
  locked: boolean;
  productUnit: string;
  quantity: number;
  setCalculationReview: (version: ICalculationReview) => void;
  removeCalculationReview: (calculationReview: ICalculationReview) => void;
  version: IVersion;
  calculationReview: ICalculationReview;
  priceSummary: any;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const units = useSelector((state: any) => state.products.units);

  const handleFormState = getFormStateHandler(
    calculationReview,
    setCalculationReview
  );

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  const installationContract = getVersionInstallationContract(
    version,
    quantity,
    installationCategories
  );

  const calculationQuantityNumeric = numericStringToFloat(
    calculationReview.quantity
  );

  // Review units installed in an hour: the review quantity is given per unit of the
  // position (e.g. 1 batch = 34,76 m²), so all of them are installed in the total hours
  const reviewUnitsPerHour =
    installationContract.totalHours > 0
      ? (calculationQuantityNumeric * quantity) /
        installationContract.totalHours
      : 0;

  return (
    <div className="calculation-sum-row">
      {/* Selling price first, then expense and margin, as in the calculation summary */}
      <Row>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Selling price`)} €`}/
            {t(
              `${translations.common}:units:${calculationReview.quantityUnit}`
            )}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {`${t(`${translations.offers}:Expense`)} €`}/
            {t(
              `${translations.common}:units:${calculationReview.quantityUnit}`
            )}
          </InputLabel>
        </Col>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} €`}/
            {t(
              `${translations.common}:units:${calculationReview.quantityUnit}`
            )}
          </InputLabel>
        </Col>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} %`}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {`${t(
              `${translations.calculationWindow}:Installation contract`
            )} €`}
            /
            {t(
              `${translations.common}:units:${calculationReview.quantityUnit}`
            )}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(
              `${translations.calculationWindow}:Contract price with social costs`
            )}{' '}
            €/
            {t(
              `${translations.common}:units:${calculationReview.quantityUnit}`
            )}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>{t(`${translations.common}:Quantity`)}</InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(
              `${translations.common}:units:${calculationReview.quantityUnit}`
            )}{' '}
            /{t(`${translations.common}:units:${H}`)}
          </InputLabel>
        </Col>
        <Col />
      </Row>
      <Row>
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="review-priceForUnit"
            unit="€"
            value={formatGroupedNumber(
              priceSummary.sellingPriceForUnit / calculationQuantityNumeric
            )}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="review-expense-sum-for-unit"
            unit="€"
            value={formatGroupedNumber(
              priceSummary.expenseSumForUnit / calculationQuantityNumeric
            )}
          />
        </Col>
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="review-contribution-margin-for-unit"
            unit="€"
            // The shown selling price minus the shown expense, so the row adds up
            value={formatGroupedNumber(
              getShownDifference(
                priceSummary.sellingPriceForUnit / calculationQuantityNumeric,
                priceSummary.expenseSumForUnit / calculationQuantityNumeric
              )
            )}
          />
        </Col>
        {/* The margin % of the position; below 20 % red, above 30 % green */}
        <Col
          className={`key-figure ${
            priceSummary.sellingPrice === 0
              ? ''
              : getMarginClass(priceSummary.contributionMarginRatio)
          }`}
        >
          <NumberWithUnitInputField
            disabled
            id="review-contribution-margin-ratio"
            unit="%"
            value={formatGroupedNumber(priceSummary.contributionMarginRatio)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="review-contribution-margin-for-unit"
            name="averageHourlyEarnings"
            onChange={handleFormState}
            unit="€"
            value={formatGroupedNumber(
              installationContract.installationsUnitInstallationPrice /
                calculationQuantityNumeric
            )}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="review-installation-contract-with-social-costs"
            unit="€"
            value={formatGroupedNumber(
              installationContract.installationsExpensePerUnit /
                calculationQuantityNumeric
            )}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            disabled={locked}
            dropdownName="quantityUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getUnitOptions(units, t)}
            dropdownValue={calculationReview.quantityUnit}
            id={`calculation-review-${calculationReview.key}-quantity`}
            maxIntegerDigits={MAX_QUANTITY_DIGITS}
            numberName="quantity"
            numberOnChange={handleFormState}
            numberValue={calculationReview.quantity}
            required
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="review-total-hours"
            unit=""
            value={formatGroupedNumber(reviewUnitsPerHour)}
          />
        </Col>
        <Col>
          {locked ? null : (
            <TrashIcon
              style={{ marginTop: '0.5rem' }}
              onClick={() => removeCalculationReview(calculationReview)}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default BatchCalculationReview;
